// src/pages/PrivacyPage.jsx  —  ohapotato v2026.10.36
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'March 2026';

export default function PrivacyPage() {
  useEffect(() => { document.title = 'Privacy Policy — OhAPotato 🥔'; window.scrollTo(0, 0); }, []);

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <Link to="/" style={s.navHome}>← Back to OhAPotato</Link>
      </nav>

      <main style={s.main}>
        <header style={s.header}>
          <h1 style={s.title}>Privacy Policy</h1>
          <p style={s.meta}>Last updated: {LAST_UPDATED}</p>
          <p style={s.intro}>
            This policy describes what information OhAPotato collects, how we use it,
            and the choices you have. We've written it in plain English because privacy
            policies shouldn't require a lawyer to read.
          </p>
        </header>

        <Section title="1. Who we are">
          <P>OhAPotato is an independent web application that helps people find recipes based on the ingredients they already have at home. We are not a large company and we have no interest in selling your data.</P>
          <P>If you have questions about this policy, contact us at <a href="mailto:hello@ohapotato.com" style={s.link}>hello@ohapotato.com</a>.</P>
        </Section>

        <Section title="2. Information we collect">
          <H3>Information you give us</H3>
          <P>If you create an account (via Auth0), we receive your email address and, if you sign in through a social provider like Google, your name and profile picture from that provider. We use this only to identify your account.</P>
          <P>If you contact us through the contact form, we receive your name, email address, and the content of your message. We use this only to respond to you.</P>

          <H3>Information stored in your browser</H3>
          <P>We store the following data in your browser's local storage — it never leaves your device unless you are signed in and have enabled cloud sync:</P>
          <ul style={s.list}>
            <li style={s.li}><strong>My Pantry</strong> — the ingredient IDs you've marked as staples.</li>
            <li style={s.li}><strong>Grocery List</strong> — items you've added from recipe cards.</li>
            <li style={s.li}><strong>Theme preference</strong> — light or dark mode.</li>
          </ul>

          <H3>Usage information</H3>
          <P>Like most web services, our servers automatically receive standard log data when you visit: your IP address, browser type, pages visited, and timestamps. We use this to keep the service running and to understand general usage patterns. This data is not sold or shared for advertising purposes.</P>
        </Section>

        <Section title="3. Cookies and advertising">
          <P>OhAPotato displays advertisements served by <strong>Google AdSense</strong> to non-premium users. Google uses cookies and similar technologies to serve ads based on your prior visits to this and other websites. Google's use of advertising cookies enables it and its partners to serve ads based on your visit to OhAPotato and/or other sites on the internet.</P>
          <P>You may opt out of personalised advertising by visiting <a href="https://www.google.com/settings/ads" style={s.link} target="_blank" rel="noreferrer">Google's Ad Settings</a> or <a href="https://www.aboutads.info" style={s.link} target="_blank" rel="noreferrer">aboutads.info</a>. For more information about how Google uses data, see <a href="https://policies.google.com/technologies/partner-sites" style={s.link} target="_blank" rel="noreferrer">How Google uses data when you use our partners' sites or apps</a>.</P>
          <P>Premium subscribers see no advertisements and no advertising cookies are loaded for their sessions.</P>
          <P>We do not use any other advertising or tracking cookies beyond those required by Google AdSense.</P>
        </Section>

        <Section title="4. Third-party services">
          <P>OhAPotato uses the following third-party services to function. Each has its own privacy policy which governs how they handle data:</P>
          <ul style={s.list}>
            <li style={s.li}><strong>Auth0</strong> — handles account creation and sign-in. <a href="https://auth0.com/privacy" style={s.link} target="_blank" rel="noreferrer">Auth0 Privacy Policy</a></li>
            <li style={s.li}><strong>Edamam</strong> — provides recipe and nutrition data in response to your ingredient searches. Ingredient queries are sent to Edamam's API. <a href="https://www.edamam.com/privacy-policy/" style={s.link} target="_blank" rel="noreferrer">Edamam Privacy Policy</a></li>
            <li style={s.li}><strong>Stripe</strong> — processes payments for Premium subscriptions. We never see or store your card details. <a href="https://stripe.com/privacy" style={s.link} target="_blank" rel="noreferrer">Stripe Privacy Policy</a></li>
            <li style={s.li}><strong>Anthropic / Claude</strong> — used as a fallback recipe generation service when external recipe databases don't return results. Queries may include the ingredient names you selected.</li>
            <li style={s.li}><strong>Google AdSense</strong> — serves advertisements to non-premium users. See section 3 above.</li>
          </ul>
        </Section>

        <Section title="5. How we use your information">
          <P>We use the information described above to:</P>
          <ul style={s.list}>
            <li style={s.li}>Provide and improve the OhAPotato service.</li>
            <li style={s.li}>Authenticate your account and maintain your session.</li>
            <li style={s.li}>Process Premium subscription payments via Stripe.</li>
            <li style={s.li}>Respond to messages you send us via the contact form.</li>
            <li style={s.li}>Understand how the service is used at an aggregate level so we can improve it.</li>
          </ul>
          <P>We do not sell your personal information. We do not use your data to build advertising profiles. We do not share your personal information with third parties except as described in sections 3 and 4.</P>
        </Section>

        <Section title="6. Data retention">
          <P>Account data is retained for as long as your account is active. If you delete your account, your profile and any cloud-synced data is deleted within 30 days.</P>
          <P>Local storage data (My Pantry, Grocery List) exists only in your browser and can be cleared at any time by clearing your browser's site data for ohapotato.com.</P>
          <P>Contact form submissions are retained for up to 12 months and then deleted.</P>
        </Section>

        <Section title="7. Your rights">
          <P>Depending on where you live, you may have the right to access, correct, or delete the personal information we hold about you. To exercise any of these rights, email us at <a href="mailto:hello@ohapotato.com" style={s.link}>hello@ohapotato.com</a> and we'll respond within 30 days.</P>
          <P>If you are in the European Economic Area, you have additional rights under the General Data Protection Regulation (GDPR), including the right to data portability and the right to lodge a complaint with your local supervisory authority.</P>
        </Section>

        <Section title="8. Children">
          <P>OhAPotato is not directed at children under 13 and we do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us with personal information, please contact us and we will delete it promptly.</P>
        </Section>

        <Section title="9. Changes to this policy">
          <P>We may update this policy from time to time. When we do, we'll update the "Last updated" date at the top. For significant changes, we'll make reasonable efforts to notify account holders by email. Your continued use of OhAPotato after a policy change constitutes your acceptance of the updated policy.</P>
        </Section>

        <footer style={s.footer}>
          <Link to="/" style={s.footerLink}>Home</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/about" style={s.footerLink}>About</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/terms" style={s.footerLink}>Terms</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/disclaimer" style={s.footerLink}>Disclaimer</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/contact" style={s.footerLink}>Contact</Link>
        </footer>
      </main>
    </div>
  );
}

// Helper sub-components for cleaner JSX
function Section({ title, children }) {
  return (
    <section style={{ marginBottom: '40px' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: '400', color: 'var(--text-primary)', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid var(--border-base)' }}>
        {title}
      </h2>
      {children}
    </section>
  );
}
function H3({ children }) {
  return <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '6px', marginTop: '18px' }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.75', marginBottom: '12px' }}>{children}</p>;
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', transition: 'background 0.3s, color 0.3s' },
  nav:  { padding: '16px 32px', borderBottom: '1px solid var(--border-base)' },
  navHome: { fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--accent-orange)', textDecoration: 'none', fontWeight: '600' },
  main: { maxWidth: '680px', margin: '0 auto', padding: '48px 24px 80px' },

  header: { marginBottom: '40px' },
  title:  { fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: '400', marginBottom: '6px', color: 'var(--text-primary)' },
  meta:   { fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' },
  intro:  { fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.7', background: 'var(--bg-card)', border: '1px solid var(--border-base)', borderRadius: 'var(--radius-md)', padding: '16px 18px' },

  list: { paddingLeft: '20px', margin: '8px 0 12px' },
  li:   { fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.75', marginBottom: '6px' },
  link: { color: 'var(--accent-orange)', textDecoration: 'none' },

  footer:     { marginTop: '64px', paddingTop: '24px', borderTop: '1px solid var(--border-base)', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' },
  footerLink: { fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--text-muted)', textDecoration: 'none' },
  footerDot:  { color: 'var(--text-muted)', fontSize: '11px' },
};
